// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject } from "react";
import { GrabberComponent, GrabberType } from "sdv/components/GrabberComponent";
import { ResizeComponent, TokenSnapBehavior } from "sdv/components/ResizeComponent";
import { TextEditorComponent } from "sdv/components/TextEditorComponent";
import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { CanvasSelectionMode } from "store/documents/types";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";

/**
 * Updates the cursor visual shown on the canvas
 */
export class CursorSystem implements ISystem {
    private readonly _sdvView: MutableRefObject<HTMLDivElement | undefined>;

    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;
    queries: Queries = {
        cursor: { types: [ComponentId.Cursor] },
        activeSelectors: { types: [ComponentId.ChunkSelector, ComponentId.CursorHit] },
        hoverGrabbers: { types: [ComponentId.Grabber, ComponentId.CursorHit] },
        activeGrabbers: { types: [ComponentId.Grabber, ComponentId.Drag] },
        resize: { types: [ComponentId.Resize] },
        tokenEditor: { types: [ComponentId.TextEditor] },
        interactables: { types: [ComponentId.Interactable, ComponentId.CursorHit] }
    }

    constructor(sdvView: MutableRefObject<HTMLDivElement | undefined>) {
        this._sdvView = sdvView;
    }

    update() {
        if (!this._sdvView.current) {
            return;
        }

        const cursorInput = this.input.getAxis2d(ActionId.SelectCursor);
        const tool = this.resources.get('tool').value;
        let targetCursor: string | undefined;
        if (!!cursorInput) {
            const resizeComp = this.queries.resize.query!.single()?.getComponent<ResizeComponent>(ComponentId.Resize)!;
            if (!!this.queries.hoverGrabbers.query!.any()) {
                const targetEntity = this.queries.activeGrabbers.query!.last() ?? this.queries.hoverGrabbers.query!.last()!;
                const grabberComp = targetEntity.getComponent<GrabberComponent>(ComponentId.Grabber)!;
                switch (grabberComp.grabber.type) {
                    case GrabberType.Bottom:
                    case GrabberType.Top:
                        targetCursor = 'ns-resize'
                        break;

                    case GrabberType.Left:
                    case GrabberType.Right:
                        targetCursor = 'ew-resize'
                        break;
                }
            }
            else if (!!this.queries.interactables.query!.any()) {
                targetCursor = 'pointer';
            }
            else if (tool === CanvasSelectionMode.Highlight && (!resizeComp?.isResizing || !!resizeComp?.isCreating)) {
                targetCursor = 'text';
            }
            else if (tool === CanvasSelectionMode.TextEdit) {
                const hasHoverToken = !!this.queries.tokenEditor.query!.single()?.getComponent<TextEditorComponent>(ComponentId.TextEditor)!.hoveredToken;
                targetCursor = hasHoverToken ? 'pointer' : 'initial';
            }
            else if (!!this.queries.activeSelectors.query!.any()) {
                targetCursor = 'cell';
            }
            else if (!!resizeComp?.isResizing && !resizeComp?.isCreating) {
                switch (resizeComp.snapBehavior) {
                    case TokenSnapBehavior.Token:
                        targetCursor = "ew-resize";
                        break;
                    case TokenSnapBehavior.Hyperline:
                        targetCursor = "ns-resize"
                        break;
                    case TokenSnapBehavior.TokenLine:
                        targetCursor = "row-resize";
                        break;
                }
            }
        }

        this._sdvView.current.style.cursor = targetCursor ?? 'initial';
    }
}
